/**
 * `/alerts/monitoring` — staff-only ingest observability.
 *
 * Two questions only: how many alerts the platform accepted, and where in the
 * timestamp chain (or which producer, or which scope) delay and missing data
 * accumulate. Everything on this page is backend-computed; the client formats,
 * sorts where the contract says to, and nothing else — a p95 recomputed from a
 * bucketed sample is a different number wearing the same name.
 *
 * The staff check here gates NAVIGATION ONLY. The backend authorizes the
 * endpoint independently and stays authoritative.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Clock, ShieldOff } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Skeleton } from "@/ui/components/ui/skeleton";
import {
  useAlertMonitoring,
  useIsStaff,
} from "@/alerts/api/queries/alerts-monitoring.queries";
import {
  buildMonitoringRangeParams,
  monitoringRangeExceedsMax,
  classifyMonitoringError,
  formatBucketSize,
  formatBucketTick,
  formatLatency,
} from "@/alerts/api/requests/alerts-monitoring.requests";
import { MONITORING_STAGE_ORDER } from "@/alerts/types/alerts-monitoring";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore";
import { AlertStageStation } from "@/alerts/components/monitoring/alert-stage-station";
import { useAlertsBreadcrumbs } from "@/alerts/hooks/useAlertsBreadcrumbs";

const Panel = ({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <section className="rounded-lg border border-border p-4">
    <div className="mb-3">
      <h2 className="text-sm font-semibold">{title}</h2>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
    {children}
  </section>
);

const Vital = ({
  label,
  value,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
}) => (
  <div>
    <p className="text-xl font-semibold tabular-nums">{value}</p>
    <p className="text-[11px] text-muted-foreground">{label}</p>
    {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
  </div>
);

const pct = (value: number | null | undefined): string =>
  value == null ? "—" : `${Math.round(value * 100)}%`;

const AlertsMonitoringPage = () => {
  const { t } = useTranslation("alerts");
  const isStaff = useIsStaff();

  // Ingest is org-wide — it has no location scope to name, so the trail stops
  // at `Org › Alerts` rather than inventing one.
  useAlertsBreadcrumbs();

  // The GLOBAL date range, like every other time-scoped page. Monitoring used to
  // own a private set of rolling presets, which meant the range shown at the top
  // of the app and the range this page actually queried could disagree with
  // nothing on screen to explain it.
  //
  // This also removes the clock from the query key entirely: the preset path
  // derived `start_time` from `Date.now()`, and before it was quantized every
  // response minted a new key and therefore another request — six in three
  // seconds against an expensive aggregate. A picker-driven window changes only
  // when the operator changes it.
  const { value: dateRange } = useDateTimeRangeStore();

  const params = useMemo(
    () => buildMonitoringRangeParams(dateRange.fromDate, dateRange.toDate),
    [dateRange],
  );
  // The backend caps the window at seven days; say so rather than quietly
  // showing less than the picker claims.
  const rangeClamped = monitoringRangeExceedsMax(dateRange.fromDate, dateRange.toDate);

  const query = useAlertMonitoring(params, isStaff);
  const data = query.data;

  const failure = query.error ? classifyMonitoringError(query.error) : null;

  const components = useMemo(() => {
    // Worst-first: missing share desc, accepted count as the tiebreak. Sorting
    // is presentation the contract asks for — no metric is derived here.
    return [...(data?.component_breakdown ?? [])].sort(
      (a, b) =>
        (b.missing_expected_timestamp_alert_share ?? 0) -
          (a.missing_expected_timestamp_alert_share ?? 0) ||
        b.accepted_alert_count - a.accepted_alert_count,
    );
  }, [data?.component_breakdown]);

  const scopeItems = useMemo(
    () =>
      [...(data?.breakdown.items ?? [])].sort(
        (a, b) =>
          (b.missing_expected_timestamp_alert_share ?? 0) -
            (a.missing_expected_timestamp_alert_share ?? 0) ||
          b.accepted_alert_count - a.accepted_alert_count,
      ),
    [data?.breakdown.items],
  );


  if (!isStaff || failure?.kind === "forbidden") {
    // Never an empty dashboard — that would read as "no activity".
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <ShieldOff className="h-6 w-6 text-muted-foreground" />
        <p className="text-sm font-medium">{t("monitoring.forbidden.title")}</p>
        <p className="max-w-sm text-xs text-muted-foreground">{t("monitoring.forbidden.hint")}</p>
      </div>
    );
  }

  if (failure?.kind === "unavailable") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <AlertTriangle className="h-6 w-6 text-destructive" />
        <p className="text-sm font-medium">{t("monitoring.unavailable.title")}</p>
        {/* Deliberately NOT zero-filled: "misconfigured" and "no data" are
            different answers and must not look alike. */}
        <p className="max-w-sm text-xs text-muted-foreground">
          {t("monitoring.unavailable.hint")}
        </p>
      </div>
    );
  }

  const trend = data?.trend;
  const bucketSeconds = trend?.bucket_seconds ?? 0;
  // Accepted volume: total_count per bucket, from the first stage series.
  const volume = trend?.series[0]?.points ?? [];
  const accepted = data?.summary.accepted_alert_count ?? 0;

  return (
    <div className="space-y-4 overflow-y-auto p-4">
      <div className="flex flex-wrap items-center gap-2">
        {/* The window comes from the app-wide date picker in the top bar. */}
        <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
          <Clock className="h-3 w-3" aria-hidden />
          {t("monitoring.basis")}
        </span>
        {rangeClamped && (
          <span role="status" className="text-[11px] text-amber-600 dark:text-amber-400">
            {t("monitoring.rangeClamped")}
          </span>
        )}
        {failure?.kind === "throttled" && (
          <span role="status" className="text-[11px] text-amber-600 dark:text-amber-400">
            {t("monitoring.throttled", { seconds: failure.retryAfterSeconds })}
          </span>
        )}
        {failure && failure.kind === "generic" && (
          <span role="status" className="text-[11px] text-muted-foreground">
            {t("monitoring.refreshFailed", { message: failure.message })}
          </span>
        )}
      </div>

      <Panel title={t("monitoring.accepted.title")} hint={t("monitoring.accepted.hint")}>
        {query.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Vital label={t("monitoring.accepted.accepted")} value={accepted} />
            <Vital
              label={t("monitoring.accepted.chainPresent")}
              value={pct(data?.summary.complete_required_chain_share)}
              sub={t("monitoring.accepted.alertsSub", {
                n: data?.summary.complete_required_chain_count ?? 0,
              })}
            />
            <Vital
              label={t("monitoring.accepted.missing")}
              value={pct(data?.summary.missing_expected_timestamp_alert_share)}
              sub={t("monitoring.accepted.alertsSub", {
                n: data?.summary.missing_expected_timestamp_alert_count ?? 0,
              })}
            />
            <Vital
              label={t("monitoring.accepted.clockSkew")}
              value={pct(data?.summary.clock_skew_alert_share)}
              sub={t("monitoring.accepted.alertsSub", {
                n: data?.summary.clock_skew_alert_count ?? 0,
              })}
            />
          </div>
        )}

        {!query.isPending && accepted === 0 && (
          <p className="mt-3 rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
            {/* Says only what this endpoint can establish — never "healthy",
                "producer offline" or "never received data". */}
            {t("monitoring.accepted.none")}{" "}
            {data?.scope_latest_accepted_at
              ? t("monitoring.accepted.lastAcceptance", { at: data.scope_latest_accepted_at })
              : t("monitoring.accepted.noAcceptance")}
          </p>
        )}
      </Panel>

      <Panel title={t("monitoring.pipeline.title")} hint={t("monitoring.pipeline.hint")}>
        {query.isPending ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {MONITORING_STAGE_ORDER.map((code) => {
              const stage = data?.stages.find((s) => s.code === code);
              if (!stage) return null;
              const points = trend?.series.find((s) => s.stage === code)?.points ?? [];
              return <AlertStageStation key={code} stage={stage} points={points} />;
            })}
          </div>
        )}
      </Panel>

      <Panel
        title={t("monitoring.volume.title")}
        hint={
          bucketSeconds
            ? t("monitoring.volume.hint", { size: formatBucketSize(bucketSeconds) })
            : undefined
        }
      >
        {query.isPending ? (
          <Skeleton className="h-48 w-full" />
        ) : volume.length === 0 ? (
          <p className="py-8 text-center text-xs text-muted-foreground">
            {t("monitoring.volume.empty")}
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={volume} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
              {/* Tick format comes from bucket_seconds, never from whether a
                  timestamp happens to land on 00:00Z — a daily bucket aligned
                  to local midnight starts at some other UTC hour. */}
              <XAxis
                dataKey="start_time"
                tickFormatter={(value: string) => formatBucketTick(value, bucketSeconds)}
                tick={{ fontSize: 11 }}
                stroke="hsl(var(--border))"
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                width={36}
                tick={{ fontSize: 11 }}
                stroke="hsl(var(--border))"
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                cursor={{ fill: "hsl(var(--muted))" }}
                labelFormatter={(value) => formatBucketTick(String(value), bucketSeconds)}
                contentStyle={{ fontSize: 12 }}
              />
              <Bar dataKey="total_count" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Panel>

      {components.length > 0 && (
        <Panel title={t("monitoring.components.title")} hint={t("monitoring.components.hint")}>
          <ul className="space-y-2">
            {components.map((component) => (
              <li key={component.source_component} className="flex items-center gap-3 text-xs">
                <span className="w-40 shrink-0 truncate font-medium">
                  {component.source_component}
                </span>
                <span className="w-16 shrink-0 tabular-nums text-muted-foreground">
                  {component.accepted_alert_count}
                </span>
                <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
                  <span
                    className="block h-full rounded-full bg-[hsl(var(--chart-2))]"
                    style={{
                      width: `${Math.round((component.complete_required_chain_share ?? 0) * 100)}%`,
                    }}
                  />
                </span>
                <span className="w-24 shrink-0 text-right tabular-nums text-muted-foreground">
                  {t("monitoring.components.missing", {
                    percent: pct(component.missing_expected_timestamp_alert_share),
                  })}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {data?.breakdown.dimension && scopeItems.length > 0 && (
        <Panel
          title={t("monitoring.scope.title", { dimension: data.breakdown.dimension })}
          hint={t("monitoring.scope.hint")}
        >
          <ul className="space-y-2">
            {scopeItems.map((item) => (
              <li
                key={`${item.machine_id ?? item.device_mxid ?? item.location_id ?? item.organization_id}`}
                className="flex items-center gap-3 text-xs"
              >
                <span className="w-40 shrink-0 truncate font-medium">
                  {item.machine_label ??
                    item.device_mxid ??
                    item.location_label ??
                    item.organization_label ??
                    "—"}
                </span>
                <span className="w-16 shrink-0 tabular-nums text-muted-foreground">
                  {item.accepted_alert_count}
                </span>
                <span className="w-28 shrink-0 tabular-nums text-muted-foreground">
                  {formatLatency(item.capture_to_ingestion_p95_ms ?? null)} p95
                </span>
                <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">
                  {t("monitoring.components.missing", {
                    percent: pct(item.missing_expected_timestamp_alert_share),
                  })}
                </span>
              </li>
            ))}
          </ul>
          {data.breakdown.truncated && (
            // Stated in text, not implied — a truncated list read as a full
            // ranking is a wrong conclusion, not a cosmetic gap.
            <p className="mt-2 text-[11px] text-amber-600 dark:text-amber-400">
              {t("monitoring.scope.truncated")}
            </p>
          )}
        </Panel>
      )}
    </div>
  );
};

export default AlertsMonitoringPage;
